import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Tooltip, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { normalizeLinkSlug } from "./surveyApiHelpers";
import { setLinkSlug } from "./surveyService";
import styles from "./style.module.scss";

// Refusals from PUT /feedback/instruments/:id/channels/link_qr/slug.
const SLUG_ERRORS = {
  slug_invalid: "surveyManagement.distribute.link.slug_invalid",
  slug_reserved: "surveyManagement.distribute.link.slug_reserved",
  slug_taken: "surveyManagement.distribute.link.slug_taken",
  link_not_created: "surveyManagement.distribute.link.slug_save_first",
};

/**
 * "Edit link" on the Link & QR card: the owner picks the name after app.pivony.com/s/. Saved
 * right away (like "Regenerate"); the old link keeps working. Only for a saved survey with a link.
 */
const LinkSlugEditor = ({ instrumentId, slug, fullUrl, onSaved }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const prefix = slug && fullUrl && fullUrl.endsWith(slug) ? fullUrl.slice(0, -slug.length) : "";
  const display = prefix.replace(/^https?:\/\//, "");
  const next = normalizeLinkSlug(draft);
  const canEdit = !!(instrumentId && slug);

  const open = () => {
    setDraft(slug || "");
    setError("");
    setEditing(true);
  };

  const save = async () => {
    if (!next || next === slug) return;
    setSaving(true);
    setError("");
    try {
      const res = await setLinkSlug(instrumentId, next);
      if (res?.channel) {
        const savedSlug = res.channel.slug || res.channel.config?.slug || next;
        onSaved({ slug: savedSlug, fullUrl: res.channel.config?.full_url || `${prefix}${savedSlug}` });
        message.success(t("surveyManagement.distribute.link.slug_saved"));
        setEditing(false);
        return;
      }
      setError(t(SLUG_ERRORS[res?.error] || "surveyManagement.msg_save_error"));
    } catch (_) {
      setError(t("surveyManagement.msg_save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Tooltip title={canEdit ? "" : t("surveyManagement.distribute.link.slug_save_first")}>
        <Button icon={<EditOutlined />} onClick={open} disabled={!canEdit}>
          {t("surveyManagement.distribute.link.edit_slug")}
        </Button>
      </Tooltip>
    );
  }

  return (
    <div className={styles.slugEditor}>
      <label className={styles.fieldLabel} htmlFor="survey-link-slug">
        {t("surveyManagement.distribute.link.slug_label")}
      </label>
      <Input
        id="survey-link-slug"
        prefix={<span className={styles.slugPrefix}>{display}</span>}
        value={draft}
        maxLength={80}
        autoFocus
        onChange={(e) => {
          setDraft(e.target.value);
          setError("");
        }}
        onPressEnter={save}
        status={error || (draft && !next) ? "error" : undefined}
      />
      <p className={styles.fieldHint}>
        {draft && !next
          ? t("surveyManagement.distribute.link.slug_rules")
          : t("surveyManagement.distribute.link.slug_preview", { url: `${display}${next || ""}` })}
      </p>
      <p className={styles.fieldHint}>{t("surveyManagement.distribute.link.slug_old_works")}</p>
      {error ? (
        <p className={styles.slugError} role="alert">
          {error}
        </p>
      ) : null}
      <div className={styles.slugActions}>
        <Button type="primary" onClick={save} loading={saving} disabled={!next || next === slug}>
          {t("surveyManagement.distribute.link.slug_save")}
        </Button>
        <Button onClick={() => setEditing(false)} disabled={saving}>
          {t("surveyManagement.distribute.link.slug_cancel")}
        </Button>
      </div>
    </div>
  );
};

export default LinkSlugEditor;
